import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-foreground text-background",
        secondary: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive: "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
        owner: "border-transparent bg-[hsl(240_38%_94%)] text-foreground hover:bg-[hsl(240_38%_94%)]",
        success: "border-transparent bg-status-success-soft text-foreground",
        error: "border-transparent bg-status-error-soft text-foreground",
        warning: "border-transparent bg-status-warning-soft text-foreground",
        info: "border-transparent bg-status-info-soft text-foreground",
        primary: "border-transparent bg-primary/15 text-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
  /**
   * AUT-435: clickable-tag affordance — 4px corners instead of the pill, a trailing chevron, and a
   * darker fill on hover. Opt-in, and deliberately so: Gabe's feedback was that a badge that opens
   * something looks identical to one that is just a label, and the fix is to mark the ones that do,
   * not to restyle every badge in the app. Static tags keep the pill and grow no chevron.
   */
  interactive?: boolean;
}

/** The clickable-tag treatment. Internal: `interactive` is the only way to ask for it. */
const interactiveBadgeClasses =
  "cursor-pointer rounded pr-1.5 hover:brightness-95 dark:hover:brightness-125";

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, interactive, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(badgeVariants({ variant }), interactive && interactiveBadgeClasses, className)}
      {...props}
    >
      {children}
      {interactive && (
        <ChevronDown className="h-3 w-3 shrink-0 opacity-70" strokeWidth={2} aria-hidden />
      )}
    </div>
  ),
);
Badge.displayName = "Badge";

export { Badge, badgeVariants };
